import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Users, CreditCard, Bell, Mail,
  Loader2, DollarSign, RefreshCw, Send, Plus, Smartphone, Play, Square, Zap,
  Trash2, ExternalLink, CheckCircle2, XCircle, MessageCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [sendingAlert, setSendingAlert] = useState(false);
  const [sendingCountryAlert, setSendingCountryAlert] = useState(null);
  const [creatingAppointment, setCreatingAppointment] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCountryAlertModal, setShowCountryAlertModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryAlertData, setCountryAlertData] = useState({
    date: '',
    location: '',
    slots: 1,
    appointment_info: '',
    destination: ''
  });
  const SUPPORTED_DESTINATIONS = ['France', 'Germany', 'Spain', 'Italy', 'Belgium', 'Portugal', 'Malta', 'Netherlands', 'Hungary', 'Iceland', 'Ireland', 'Switzerland', 'Austria'];
  const [newAppointment, setNewAppointment] = useState({
    country_to: 'France',
    location: 'Tunis TLS Contact Center',
    date: '',
    slots: 1
  });
  const [slotifyStatus, setSlotifyStatus] = useState({ status: 'stopped' });
  const [slotifyLoading, setSlotifyLoading] = useState(false);
  const [sendingWelcomeEmail, setSendingWelcomeEmail] = useState(false);
  const [emailQueueStatus, setEmailQueueStatus] = useState(null);
  const [activatingUserId, setActivatingUserId] = useState(null);
  const [dhmadLinks, setDhmadLinks] = useState([]);
  const [dhmadLinksInput, setDhmadLinksInput] = useState('');
  const [savingDhmadLinks, setSavingDhmadLinks] = useState(false);
  const [togglingDhmadLinkId, setTogglingDhmadLinkId] = useState(null);
  const [deletingDhmadLinkId, setDeletingDhmadLinkId] = useState(null);

  useEffect(() => {
    fetchAllData();
    fetchSlotifyStatus();
    fetchEmailQueueStatus();
    fetchDhmadLinks();
    fetchChatSessions();
  }, []);

  const fetchDhmadLinks = async () => {
    try {
      const response = await axios.get(`${API}/admin/payment/dhmad-links`);
      setDhmadLinks(response.data?.links || []);
    } catch (error) {
      // stumm fehlschlagen, Liste bleibt leer
    }
  };

  const handleAddDhmadLinks = async () => {
    const urls = dhmadLinksInput
      .split('\n')
      .map((u) => u.trim())
      .filter(Boolean);
    if (urls.length === 0) return;
    setSavingDhmadLinks(true);
    try {
      const response = await axios.post(`${API}/admin/payment/dhmad-links`, { urls });
      toast.success(`${response.data?.added ?? urls.length} Link(s) hinzugefügt!`);
      setDhmadLinksInput('');
      fetchDhmadLinks();
    } catch (error) {
      toast.error('Hinzufügen fehlgeschlagen');
    } finally {
      setSavingDhmadLinks(false);
    }
  };

  const handleToggleDhmadLink = async (link) => {
    const nextStatus = link.status === 'active' ? 'inactive' : 'active';
    setTogglingDhmadLinkId(link.id);
    try {
      await axios.patch(`${API}/admin/payment/dhmad-links/${link.id}`, { status: nextStatus });
      fetchDhmadLinks();
    } catch (error) {
      toast.error('Status ändern fehlgeschlagen');
    } finally {
      setTogglingDhmadLinkId(null);
    }
  };

  const handleDeleteDhmadLink = async (link) => {
    if (!window.confirm('Diesen Link endgültig entfernen?')) return;
    setDeletingDhmadLinkId(link.id);
    try {
      await axios.delete(`${API}/admin/payment/dhmad-links/${link.id}`);
      fetchDhmadLinks();
    } catch (error) {
      toast.error('Löschen fehlgeschlagen');
    } finally {
      setDeletingDhmadLinkId(null);
    }
  };

  // Der Link, der Kunden gerade tatsächlich angezeigt wird: der älteste
  // aktive Link in der Liste (die Liste kommt bereits sortiert vom Server).
  const currentActiveDhmadLinkId = dhmadLinks.find((l) => l.status === 'active')?.id;

  const [chatSessions, setChatSessions] = useState([]);
  const [loadingChatSessions, setLoadingChatSessions] = useState(false);
  const [openChatSessionId, setOpenChatSessionId] = useState(null);
  const [chatSessionMessages, setChatSessionMessages] = useState([]);
  const [loadingChatMessages, setLoadingChatMessages] = useState(false);

  const fetchChatSessions = async () => {
    setLoadingChatSessions(true);
    try {
      const response = await axios.get(`${API}/admin/chat-sessions`);
      setChatSessions(response.data?.sessions || []);
    } catch (error) {
      // stumm fehlschlagen
    } finally {
      setLoadingChatSessions(false);
    }
  };

  const openChatSession = async (sessionId) => {
    setOpenChatSessionId(sessionId);
    setLoadingChatMessages(true);
    setChatSessionMessages([]);
    try {
      const response = await axios.get(`${API}/admin/chat-sessions/${sessionId}/messages`);
      setChatSessionMessages(response.data?.messages || []);
    } catch (error) {
      toast.error('Chat-Verlauf konnte nicht geladen werden');
    } finally {
      setLoadingChatMessages(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, transactionsRes, notificationsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/transactions`),
        axios.get(`${API}/admin/notifications`),
        axios.get(`${API}/admin/contacts`)
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setTransactions(transactionsRes.data);
      setNotifications(notificationsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateSubscription = async (user) => {
    if (!window.confirm(
      `Abo für ${user.name} (${user.email}) manuell freischalten?\n\n` +
      `Nur bestätigen, wenn die Zahlung (z.B. über DHMAD.tn) tatsächlich eingegangen ist.`
    )) {
      return;
    }
    setActivatingUserId(user.id);
    try {
      await axios.post(`${API}/admin/users/${user.id}/activate-subscription`);
      toast.success(`Abo für ${user.name} wurde freigeschaltet!`);
      fetchAllData();
    } catch (error) {
      toast.error('Freischalten fehlgeschlagen');
    } finally {
      setActivatingUserId(null);
    }
  };

  const handleSendAlert = async () => {
    setSendingAlert(true);
    try {
      const response = await axios.post(`${API}/admin/send-alert`);
      toast.success(`Alert sent! Emails: ${response.data.emails_sent}, SMS: ${response.data.sms_sent}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to send alert');
    } finally {
      setSendingAlert(false);
    }
  };

  const handleSendCountryAlert = async (country) => {
    setSelectedCountry(country);
    setShowCountryAlertModal(true);
  };

  const submitCountryAlert = async () => {
    if (!selectedCountry) return;
    
    setSendingCountryAlert(selectedCountry);
    try {
      // If "all" selected, don't send destination filter
      const payload = {
        ...countryAlertData,
        destination: countryAlertData.destination === 'all' ? null : countryAlertData.destination || null
      };
      const response = await axios.post(`${API}/admin/send-alert/${selectedCountry.toLowerCase()}`, payload);
      const destLabel = countryAlertData.destination && countryAlertData.destination !== 'all' 
        ? ` (${countryAlertData.destination})` : '';
      toast.success(
        `Alert sent to ${selectedCountry}${destLabel}! Emails: ${response.data.emails_sent}, SMS: ${response.data.sms_sent}`,
        { duration: 5000 }
      );
      setShowCountryAlertModal(false);
      setCountryAlertData({ date: '', location: '', slots: 1, appointment_info: '', destination: '' });
      fetchAllData();
    } catch (error) {
      toast.error(`Failed to send alert to ${selectedCountry}`);
    } finally {
      setSendingCountryAlert(null);
    }
  };

  const handleCreateAppointment = async () => {
    if (!newAppointment.date) {
      toast.error('Please select a date');
      return;
    }
    
    setCreatingAppointment(true);
    try {
      const response = await axios.post(`${API}/admin/appointments/create`, newAppointment);
      toast.success(
        `Appointment created! Emails: ${response.data.notifications.emails_sent}, SMS: ${response.data.notifications.sms_sent}`,
        { duration: 5000 }
      );
      setShowCreateModal(false);
      setNewAppointment({
        country_to: 'France',
        location: 'Tunis TLS Contact Center',
        date: '',
        slots: 1
      });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to create appointment');
    } finally {
      setCreatingAppointment(false);
    }
  };

  // Slotify Bot Functions for UK/Ireland
  const fetchSlotifyStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/slotify/status`);
      setSlotifyStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch slotify status');
    }
  };

  const handleStartSlotify = async () => {
    setSlotifyLoading(true);
    try {
      const response = await axios.post(`${API}/admin/slotify/start`);
      toast.success('Slotify UK/Ireland checker started! Checking every 15 minutes.');
      setSlotifyStatus({ ...slotifyStatus, status: 'running' });
      fetchSlotifyStatus();
    } catch (error) {
      toast.error('Failed to start slotify checker');
    } finally {
      setSlotifyLoading(false);
    }
  };

  const handleStopSlotify = async () => {
    setSlotifyLoading(true);
    try {
      const response = await axios.post(`${API}/admin/slotify/stop`);
      toast.success('Slotify UK/Ireland checker stopped.');
      setSlotifyStatus({ ...slotifyStatus, status: 'stopped' });
    } catch (error) {
      toast.error('Failed to stop slotify checker');
    } finally {
      setSlotifyLoading(false);
    }
  };

  const handleCheckNow = async () => {
    setSlotifyLoading(true);
    try {
      const response = await axios.post(`${API}/admin/slotify/check-now`);
      toast.success('Manual UK/Ireland check started!');
    } catch (error) {
      toast.error('Failed to trigger manual check');
    } finally {
      setSlotifyLoading(false);
    }
  };

  const handleSendWelcomeEmail = async () => {
    setSendingWelcomeEmail(true);
    try {
      const response = await axios.post(`${API}/admin/send-welcome-email`);
      toast.success(
        `${response.data.queued_count} Welcome-Emails in Queue! Geschätzte Zeit: ${response.data.estimated_time}`,
        { duration: 8000 }
      );
      fetchEmailQueueStatus();
    } catch (error) {
      toast.error('Failed to queue welcome emails');
    } finally {
      setSendingWelcomeEmail(false);
    }
  };

  const [sendingReminderEmail, setSendingReminderEmail] = useState(false);

  const handleSendReminderEmail = async () => {
    setSendingReminderEmail(true);
    try {
      const response = await axios.post(`${API}/admin/send-reminder-email`);
      toast.success(
        `${response.data.emails_queued} Reminder-Emails in Queue! Start Queue to send.`,
        { duration: 8000 }
      );
      fetchEmailQueueStatus();
    } catch (error) {
      toast.error('Failed to queue reminder emails');
    } finally {
      setSendingReminderEmail(false);
    }
  };

  const fetchEmailQueueStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-queue/status`);
      setEmailQueueStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch email queue status');
    }
  };

  const handleStartEmailQueue = async () => {
    try {
      await axios.post(`${API}/admin/email-queue/start`);
      toast.success('Email Queue gestartet!');
      fetchEmailQueueStatus();
    } catch (error) {
      toast.error('Failed to start email queue');
    }
  };

  const handleStopEmailQueue = async () => {
    try {
      await axios.post(`${API}/admin/email-queue/stop`);
      toast.success('Email Queue gestoppt!');
      fetchEmailQueueStatus();
    } catch (error) {
      toast.error('Failed to stop email queue');
    }
  };

  const handleRetryFailedEmails = async () => {
    try {
      const response = await axios.post(`${API}/admin/email-queue/retry-failed`);
      toast.success(`${response.data.message}`);
      fetchEmailQueueStatus();
    } catch (error) {
      toast.error('Failed to retry emails');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#0A0A0A]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="admin-dashboard">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-[#0A0A0A]" data-testid="admin-title">
                {t('admin_title')}
              </h1>
              <p className="text-gray-600 mt-1">Manage users, subscriptions, and notifications</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="outline" 
                onClick={fetchAllData}
                className="rounded-sm"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              
              {/* Send Alert Button */}
              <Button 
                className="bg-[#0A0A0A] hover:bg-[#0A0A0A]/90 text-white rounded-sm"
                onClick={handleSendAlert}
                disabled={sendingAlert}
                data-testid="send-alert-btn"
              >
                {sendingAlert ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Alert to All
              </Button>
              
              {/* Welcome Email Button */}
              <Button 
                className="bg-[#22C55E] hover:bg-[#22C55E]/90 text-white rounded-sm"
                onClick={handleSendWelcomeEmail}
                disabled={sendingWelcomeEmail}
                data-testid="send-welcome-email-btn"
              >
                {sendingWelcomeEmail ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Welcome Email
              </Button>
              
              {/* Reminder Email Button for non-subscribed users */}
              <Button 
                className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-white rounded-sm"
                onClick={handleSendReminderEmail}
                disabled={sendingReminderEmail}
                data-testid="send-reminder-email-btn"
              >
                {sendingReminderEmail ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Reminder Email
              </Button>
            </div>
          </div>

          {/* DHMAD.tn Zahlungslink-Vorrat (Tunesien) */}
          <Card className="mb-6 border-2 border-dashed border-green-300 bg-green-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                🇹🇳 DHMAD-Zahlungslinks (Tunesien)
              </CardTitle>
              <p className="text-sm text-gray-600">
                Jeder DHMAD-Einladungslink ist nur einmal gültig. Trag hier morgens einen
                ganzen Vorrat auf einmal ein (einen Link pro Zeile). Kunden bekommen immer
                den ältesten <strong>aktiven</strong> Link angezeigt. Sobald einer bezahlt
                wurde, setz ihn unten auf <strong>Inaktiv</strong> — der nächste aktive Link
                rutscht dann automatisch nach.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-2">
                <Textarea
                  value={dhmadLinksInput}
                  onChange={(e) => setDhmadLinksInput(e.target.value)}
                  placeholder={"https://dhmad.tn/invite/...\nhttps://dhmad.tn/invite/...\nhttps://dhmad.tn/invite/..."}
                  rows={4}
                  className="flex-1"
                  data-testid="dhmad-links-input"
                />
                <Button
                  onClick={handleAddDhmadLinks}
                  disabled={savingDhmadLinks || !dhmadLinksInput.trim()}
                  className="bg-green-600 hover:bg-green-700 text-white self-start"
                  data-testid="dhmad-links-add-btn"
                >
                  {savingDhmadLinks ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Link(s) hinzufügen
                </Button>
              </div>

              {dhmadLinks.length > 0 ? (
                <div className="mt-4 space-y-1.5">
                  {dhmadLinks.map((link) => {
                    const isCurrent = link.id === currentActiveDhmadLinkId;
                    return (
                      <div
                        key={link.id}
                        className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm ${
                          isCurrent
                            ? 'border-green-400 bg-white shadow-sm'
                            : link.status === 'active'
                            ? 'border-gray-200 bg-white/60'
                            : 'border-gray-200 bg-gray-100/60 opacity-60'
                        }`}
                        data-testid={`dhmad-link-row-${link.id}`}
                      >
                        <div className="min-w-0 flex-1 flex items-center gap-2">
                          {isCurrent && (
                            <span className="shrink-0 rounded-full bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-wide">
                              Aktuell
                            </span>
                          )}
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="truncate underline text-gray-700 hover:text-green-700"
                            title={link.url}
                          >
                            {link.url}
                          </a>
                          <ExternalLink className="w-3 h-3 text-gray-400 shrink-0" />
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleDhmadLink(link)}
                            disabled={togglingDhmadLinkId === link.id}
                            className={link.status === 'active' ? 'text-amber-700 border-amber-300' : 'text-green-700 border-green-300'}
                            data-testid={`dhmad-link-toggle-${link.id}`}
                          >
                            {togglingDhmadLinkId === link.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : link.status === 'active' ? (
                              <>
                                <XCircle className="w-3.5 h-3.5 mr-1" /> Inaktiv setzen
                              </>
                            ) : (
                              <>
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Aktiv setzen
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteDhmadLink(link)}
                            disabled={deletingDhmadLinkId === link.id}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`dhmad-link-delete-${link.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 mt-4">
                  Noch keine Links im Vorrat — oben welche einfügen.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Chatbot-Unterhaltungen */}
          <Card className="mb-6 border-2 border-dashed border-blue-300 bg-blue-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-600" />
                Chatbot-Unterhaltungen
              </CardTitle>
              <p className="text-sm text-gray-600">
                Was Besucher (Landing Page) und Kunden (Dashboard) dem FAQ-Chatbot fragen.
                Neueste zuerst.
              </p>
            </CardHeader>
            <CardContent>
              {loadingChatSessions ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : chatSessions.length === 0 ? (
                <p className="text-sm text-gray-500">Noch keine Chat-Unterhaltungen.</p>
              ) : (
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {chatSessions.map((session) => (
                    <button
                      key={session.session_id}
                      type="button"
                      onClick={() => openChatSession(session.session_id)}
                      className="w-full flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-sm hover:border-blue-300 hover:bg-blue-50/60 transition-colors"
                      data-testid={`chat-session-row-${session.session_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] uppercase">
                            {session.page === 'dashboard' ? 'Dashboard' : 'Landing'}
                          </Badge>
                          {session.user_email && (
                            <span className="text-xs text-gray-500 truncate">{session.user_email}</span>
                          )}
                          <span className="text-xs text-gray-400">
                            {session.message_count} Nachricht{session.message_count === 1 ? '' : 'en'}
                          </span>
                        </div>
                        <p className="text-gray-700 truncate mt-0.5">
                          {session.last_sender === 'user' ? '🗣️ ' : '🤖 '}
                          {session.last_message}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs text-gray-400">
                        {new Date(session.last_message_at).toLocaleString()}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Chat-Verlauf Dialog */}
          <Dialog open={!!openChatSessionId} onOpenChange={(isOpen) => !isOpen && setOpenChatSessionId(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Chat-Verlauf</DialogTitle>
                <DialogDescription className="text-xs break-all">
                  Session: {openChatSessionId}
                </DialogDescription>
              </DialogHeader>
              {loadingChatMessages ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : (
                <ScrollArea className="max-h-[60vh] pr-2">
                  <div className="flex flex-col gap-2">
                    {chatSessionMessages.map((m) => (
                      <div
                        key={m.id}
                        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                          m.sender === 'user'
                            ? 'self-end bg-[#FF3B30] text-white rounded-br-sm'
                            : 'self-start bg-gray-100 text-gray-800 rounded-bl-sm'
                        }`}
                      >
                        {m.message}
                        <div className={`text-[10px] mt-1 ${m.sender === 'user' ? 'text-white/70' : 'text-gray-400'}`}>
                          {new Date(m.created_at).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </DialogContent>
          </Dialog>

          {/* Country-Specific Alert Buttons */}
          <Card className="mb-6 border-2 border-dashed border-[#FF3B30]/30 bg-[#FF3B30]/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#FF3B30]" />
                Quick Country Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {/* Tunisia Alert */}
                <Button 
                  className="bg-[#E70013] hover:bg-[#E70013]/90 text-white rounded-sm"
                  onClick={() => handleSendCountryAlert('Tunisia')}
                  disabled={sendingCountryAlert === 'Tunisia'}
                  data-testid="alert-tunisia-btn"
                >
                  {sendingCountryAlert === 'Tunisia' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <span className="mr-2">🇹🇳</span>
                  )}
                  Tunisia
                </Button>
                
                {/* Morocco Alert */}
                <Button 
                  className="bg-[#C1272D] hover:bg-[#C1272D]/90 text-white rounded-sm"
                  onClick={() => handleSendCountryAlert('Morocco')}
                  disabled={sendingCountryAlert === 'Morocco'}
                  data-testid="alert-morocco-btn"
                >
                  {sendingCountryAlert === 'Morocco' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <span className="mr-2">🇲🇦</span>
                  )}
                  Morocco
                </Button>
                
                {/* Egypt Alert */}
                <Button 
                  className="bg-[#CE1126] hover:bg-[#CE1126]/90 text-white rounded-sm"
                  onClick={() => handleSendCountryAlert('Egypt')}
                  disabled={sendingCountryAlert === 'Egypt'}
                  data-testid="alert-egypt-btn"
                >
                  {sendingCountryAlert === 'Egypt' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <span className="mr-2">🇪🇬</span>
                  )}
                  Egypt
                </Button>

                {/* UK Alert */}
                <Button 
                  className="bg-[#012169] hover:bg-[#012169]/90 text-white rounded-sm"
                  onClick={() => handleSendCountryAlert('UK')}
                  disabled={sendingCountryAlert === 'UK'}
                  data-testid="alert-uk-btn"
                >
                  {sendingCountryAlert === 'UK' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <span className="mr-2">🇬🇧</span>
                  )}
                  UK
                </Button>

                {/* Ireland Alert */}
                <Button 
                  className="bg-[#169B62] hover:bg-[#169B62]/90 text-white rounded-sm"
                  onClick={() => handleSendCountryAlert('Ireland')}
                  disabled={sendingCountryAlert === 'Ireland'}
                  data-testid="alert-ireland-btn"
                >
                  {sendingCountryAlert === 'Ireland' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <span className="mr-2">🇮🇪</span>
                  )}
                  Ireland
                </Button>

                {/* Algeria Alert */}
                <Button 
                  className="bg-[#006233] hover:bg-[#006233]/90 text-white rounded-sm"
                  onClick={() => handleSendCountryAlert('Algeria')}
                  disabled={sendingCountryAlert === 'Algeria'}
                  data-testid="alert-algeria-btn"
                >
                  {sendingCountryAlert === 'Algeria' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <span className="mr-2">🇩🇿</span>
                  )}
                  Algeria
                </Button>

                {/* Nigeria Alert */}
                <Button 
                  className="bg-[#008751] hover:bg-[#008751]/90 text-white rounded-sm"
                  onClick={() => handleSendCountryAlert('Nigeria')}
                  disabled={sendingCountryAlert === 'Nigeria'}
                  data-testid="alert-nigeria-btn"
                >
                  {sendingCountryAlert === 'Nigeria' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <span className="mr-2">🇳🇬</span>
                  )}
                  Nigeria
                </Button>

                {/* Thailand Alert */}
                <Button 
                  className="bg-[#0067B1] hover:bg-[#0067B1]/90 text-white rounded-sm"
                  onClick={() => handleSendCountryAlert('Thailand')}
                  disabled={sendingCountryAlert === 'Thailand'}
                  data-testid="alert-thailand-btn"
                >
                  {sendingCountryAlert === 'Thailand' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <span className="mr-2">🇹🇭</span>
                  )}
                  Thailand
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Click a button to send SMS & Email alerts only to subscribers from that country (TLS, VFS Global, BLS).
              </p>
            </CardContent>
          </Card>

          {/* Email Queue Status */}
          <Card className="mb-6 border-2 border-dashed border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Mail className="w-5 h-5 text-orange-600" />
                Email Queue Status
                <span className={`ml-2 text-xs px-2 py-1 rounded-full ${
                  emailQueueStatus?.queue_processor_running 
                    ? 'bg-green-100 text-green-700 animate-pulse' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {emailQueueStatus?.queue_processor_running ? '● Läuft' : '○ Gestoppt'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {emailQueueStatus ? (
                <>
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div className="bg-white p-3 rounded-lg border text-center">
                      <p className="text-2xl font-bold text-yellow-600">{emailQueueStatus.statistics?.pending || 0}</p>
                      <p className="text-xs text-gray-500">Wartend</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border text-center">
                      <p className="text-2xl font-bold text-green-600">{emailQueueStatus.statistics?.sent || 0}</p>
                      <p className="text-xs text-gray-500">Gesendet</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border text-center">
                      <p className="text-2xl font-bold text-red-600">{emailQueueStatus.statistics?.failed || 0}</p>
                      <p className="text-xs text-gray-500">Fehlgeschlagen</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border text-center">
                      <p className="text-2xl font-bold text-blue-600">{emailQueueStatus.estimated_completion}</p>
                      <p className="text-xs text-gray-500">Geschätzte Zeit</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {!emailQueueStatus.queue_processor_running ? (
                      <Button 
                        className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
                        onClick={handleStartEmailQueue}
                        data-testid="start-queue-btn"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Queue Starten
                      </Button>
                    ) : (
                      <Button 
                        className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
                        onClick={handleStopEmailQueue}
                        data-testid="stop-queue-btn"
                      >
                        <Square className="w-4 h-4 mr-2" />
                        Queue Stoppen
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      className="rounded-sm"
                      onClick={fetchEmailQueueStatus}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Aktualisieren
                    </Button>
                    {emailQueueStatus.statistics?.failed > 0 && (
                      <Button 
                        variant="outline"
                        className="rounded-sm border-red-300 text-red-600 hover:bg-red-50"
                        onClick={handleRetryFailedEmails}
                      >
                        Fehlgeschlagene Wiederholen
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    1 Email pro Minute • Max. 1.440 Emails/Tag • Keine Rate-Limits
                  </p>
                </>
              ) : (
                <p className="text-gray-500">Lade Queue-Status...</p>
              )}
            </CardContent>
          </Card>

          {/* Slotify Bot UK/Ireland Auto-Checker */}
          <Card className="mb-6 border-2 border-dashed border-[#012169]/30 bg-gradient-to-r from-[#012169]/5 to-[#169B62]/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#012169]" />
                UK/Ireland Alerts (via @slotify_official_bot)
                <span className="ml-2 text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                  Webhook Active
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                <strong>So funktioniert es:</strong>
              </p>
              <ol className="text-sm text-gray-600 mb-4 list-decimal list-inside space-y-1">
                <li>Abonnieren Sie bei <span className="font-mono bg-gray-100 px-1">@slotify_official_bot</span> für UK/Ireland Termine</li>
                <li>Wenn der Bot Ihnen Termine schickt, <strong>leiten Sie die Nachricht weiter</strong> an <span className="font-mono bg-gray-100 px-1">@AlertsTunisiaBot</span></li>
                <li>Das System sendet automatisch <strong>Email-Alerts</strong> an alle relevanten UK/Ireland User</li>
              </ol>
              <div className="flex flex-wrap gap-3">
                <Button 
                  variant="outline"
                  className="rounded-sm border-[#012169] text-[#012169] hover:bg-[#012169]/10"
                  onClick={() => window.open('https://t.me/slotify_official_bot', '_blank')}
                  data-testid="open-slotify-btn"
                >
                  <span className="mr-2">🤖</span>
                  @slotify_official_bot öffnen
                </Button>
                <Button 
                  variant="outline"
                  className="rounded-sm border-[#012169] text-[#012169] hover:bg-[#012169]/10"
                  onClick={() => window.open('https://t.me/AlertsTunisiaBot', '_blank')}
                  data-testid="open-alerts-bot-btn"
                >
                  <span className="mr-2">📬</span>
                  @AlertsTunisiaBot öffnen
                </Button>
              </div>
              <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  🇬🇧 UK: London, Manchester, Birmingham, Edinburgh, Glasgow
                </span>
                <span className="flex items-center gap-1">
                  🇮🇪 Ireland: Dublin, Cork, Galway
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Country Alert Modal */}
          <Dialog open={showCountryAlertModal} onOpenChange={setShowCountryAlertModal}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {selectedCountry === 'Tunisia' && '🇹🇳 '}
                  {selectedCountry === 'Morocco' && '🇲🇦 '}
                  {selectedCountry === 'Egypt' && '🇪🇬 '}
                  {selectedCountry === 'UK' && '🇬🇧 '}
                  {selectedCountry === 'Ireland' && '🇮🇪 '}
                  {selectedCountry === 'Algeria' && '🇩🇿 '}
                  {selectedCountry === 'Nigeria' && '🇳🇬 '}
                  {selectedCountry === 'Thailand' && '🇹🇭 '}
                  Send Alert to {selectedCountry}
                </DialogTitle>
                <DialogDescription>
                  Send SMS & Email to {selectedCountry} subscribers. Filter by destination to target specific users.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="alert-destination" className="font-semibold">Destination Country <span className="text-red-500">*</span></Label>
                  <Select
                    value={countryAlertData.destination}
                    onValueChange={(value) => setCountryAlertData(prev => ({...prev, destination: value}))}
                  >
                    <SelectTrigger id="alert-destination" data-testid="alert-destination-select">
                      <SelectValue placeholder="Select destination (e.g., France, Spain)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="destination-all">All Destinations</SelectItem>
                      {SUPPORTED_DESTINATIONS.map(dest => (
                        <SelectItem key={dest} value={dest} data-testid={`destination-${dest.toLowerCase()}`}>{dest}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Only users with this destination preference will receive the alert. "All" sends to everyone in {selectedCountry}.</p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="alert-location">Location (optional)</Label>
                  <Input
                    id="alert-location"
                    data-testid="alert-location-input"
                    placeholder="e.g., London, Tunis TLS, Sfax TLS"
                    value={countryAlertData.location}
                    onChange={(e) => setCountryAlertData(prev => ({...prev, location: e.target.value}))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="alert-date">Date (optional)</Label>
                  <Input
                    id="alert-date"
                    data-testid="alert-date-input"
                    type="date"
                    value={countryAlertData.date}
                    onChange={(e) => setCountryAlertData(prev => ({...prev, date: e.target.value}))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="alert-slots">Number of Slots</Label>
                  <Input
                    id="alert-slots"
                    data-testid="alert-slots-input"
                    type="number"
                    min="1"
                    value={countryAlertData.slots}
                    onChange={(e) => setCountryAlertData(prev => ({...prev, slots: parseInt(e.target.value) || 1}))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="alert-info">Additional Info (optional)</Label>
                  <Input
                    id="alert-info"
                    data-testid="alert-info-input"
                    placeholder="e.g., Long Stay Visa, Student Visa"
                    value={countryAlertData.appointment_info}
                    onChange={(e) => setCountryAlertData(prev => ({...prev, appointment_info: e.target.value}))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCountryAlertModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={submitCountryAlert}
                  disabled={sendingCountryAlert}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
                >
                  {sendingCountryAlert ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Alert
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="rounded-sm border-0 shadow-sm stats-card" data-testid="stat-total-users">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{t('admin_total_users')}</p>
                    <p className="text-3xl font-heading font-bold text-[#0A0A0A]">{stats?.total_users || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-sm border-0 shadow-sm stats-card" data-testid="stat-active-subs">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{t('admin_active_subs')}</p>
                    <p className="text-3xl font-heading font-bold text-[#0A0A0A]">{stats?.subscribed_users || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-sm flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-sm border-0 shadow-sm stats-card" data-testid="stat-revenue">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{t('admin_revenue')}</p>
                    <p className="text-3xl font-heading font-bold text-[#0A0A0A]">€{stats?.revenue || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 rounded-sm flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-sm border-0 shadow-sm stats-card" data-testid="stat-alerts-sent">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{t('admin_alerts_sent')}</p>
                    <p className="text-3xl font-heading font-bold text-[#0A0A0A]">{stats?.total_notifications || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-sm flex items-center justify-center">
                    <Bell className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="users" className="space-y-4">
            <TabsList className="bg-white border rounded-sm">
              <TabsTrigger value="users" className="rounded-sm" data-testid="tab-users">
                <Users className="w-4 h-4 mr-2" />
                {t('admin_users')}
              </TabsTrigger>
              <TabsTrigger value="preferences" className="rounded-sm" data-testid="tab-preferences">
                <Smartphone className="w-4 h-4 mr-2" />
                Preferences
              </TabsTrigger>
              <TabsTrigger value="transactions" className="rounded-sm" data-testid="tab-transactions">
                <CreditCard className="w-4 h-4 mr-2" />
                {t('admin_transactions')}
              </TabsTrigger>
              <TabsTrigger value="notifications" className="rounded-sm" data-testid="tab-notifications">
                <Bell className="w-4 h-4 mr-2" />
                {t('admin_notifications')}
              </TabsTrigger>
              <TabsTrigger value="contacts" className="rounded-sm" data-testid="tab-contacts">
                <Mail className="w-4 h-4 mr-2" />
                {t('admin_contacts')}
              </TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users">
              <Card className="rounded-sm border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading">All Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Subscribed</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Visa Centre</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Aktion</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user, idx) => (
                        <TableRow key={user.id || idx}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            {user.is_subscribed ? (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-sm">Active</span>
                            ) : (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-sm">Inactive</span>
                            )}
                          </TableCell>
                          <TableCell>{user.preferences?.residence_country || 'N/A'}</TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-1 rounded-sm font-medium ${
                              user.preferences?.visa_center === 'TLS' ? 'bg-red-100 text-red-700' :
                              user.preferences?.visa_center === 'VFS Global' ? 'bg-blue-100 text-blue-700' :
                              user.preferences?.visa_center === 'BLS' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {user.preferences?.visa_center || 'TLS'}
                            </span>
                          </TableCell>
                          <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {!user.is_subscribed && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-8 border-green-300 text-green-700 hover:bg-green-50"
                                disabled={activatingUserId === user.id}
                                onClick={() => handleActivateSubscription(user)}
                                data-testid={`activate-sub-btn-${user.id}`}
                              >
                                {activatingUserId === user.id ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <DollarSign className="w-3 h-3 mr-1" />
                                )}
                                Abo freischalten
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {users.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                            No users yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Preferences Tab - Only ACTIVE (paid) user preferences */}
            <TabsContent value="preferences">
              <Card className="rounded-sm border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                    Active Subscribers Preferences
                  </CardTitle>
                  <p className="text-sm text-gray-500">Only showing users with active subscriptions</p>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Residence</TableHead>
                        <TableHead>Destination</TableHead>
                        <TableHead>Visa Centre</TableHead>
                        <TableHead>Notification</TableHead>
                        <TableHead>Phone</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.filter(u => u.is_subscribed).map((user, idx) => (
                        <TableRow key={user.id || idx}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{user.name}</p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{user.preferences?.residence_country || 'N/A'}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{user.preferences?.destination_country || 'France'}</span>
                          </TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-1 rounded-sm font-bold ${
                              user.preferences?.visa_center === 'TLS' ? 'bg-red-500 text-white' :
                              user.preferences?.visa_center === 'VFS Global' ? 'bg-blue-500 text-white' :
                              user.preferences?.visa_center === 'BLS' ? 'bg-green-500 text-white' :
                              'bg-gray-500 text-white'
                            }`}>
                              {user.preferences?.visa_center || 'TLS'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-1 rounded-sm ${
                              user.preferences?.notification_type === 'both' ? 'bg-purple-100 text-purple-700' :
                              user.preferences?.notification_type === 'sms' ? 'bg-orange-100 text-orange-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {user.preferences?.notification_type === 'both' ? 'SMS + Email' :
                               user.preferences?.notification_type === 'sms' ? 'SMS Only' : 'Email Only'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm font-mono">
                              {user.preferences?.phone_number || '-'}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                      {users.filter(u => u.is_subscribed).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                            No active subscribers yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions">
              <Card className="rounded-sm border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading">Payment Transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Session ID</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Payment Status</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((tx, idx) => (
                        <TableRow key={tx.id || idx}>
                          <TableCell className="font-mono text-sm">{tx.session_id?.slice(0, 20)}...</TableCell>
                          <TableCell>€{tx.amount}</TableCell>
                          <TableCell>{tx.status}</TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-1 rounded-sm ${
                              tx.payment_status === 'paid' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {tx.payment_status}
                            </span>
                          </TableCell>
                          <TableCell>{new Date(tx.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                      {transactions.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                            No transactions yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications">
              <Card className="rounded-sm border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading">Sent Notifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Sent</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {notifications.map((notif, idx) => (
                        <TableRow key={notif.id || idx}>
                          <TableCell>
                            <span className={`text-xs px-2 py-1 rounded-sm ${
                              notif.type === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                            }`}>
                              {notif.type}
                            </span>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{notif.message?.replace(/<[^>]*>/g, '').slice(0, 50)}...</TableCell>
                          <TableCell>{notif.status}</TableCell>
                          <TableCell>{new Date(notif.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                      {notifications.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                            No notifications sent yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Contacts Tab */}
            <TabsContent value="contacts">
              <Card className="rounded-sm border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading">Contact Messages</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Received</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contacts.map((contact, idx) => (
                        <TableRow key={contact.id || idx}>
                          <TableCell className="font-medium">{contact.name}</TableCell>
                          <TableCell>{contact.email}</TableCell>
                          <TableCell>{contact.subject}</TableCell>
                          <TableCell className="max-w-xs truncate">{contact.message?.slice(0, 50)}...</TableCell>
                          <TableCell>{new Date(contact.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                      {contacts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                            No contact messages yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
